import { Button, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Stack, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { adminUpdateCompanyHomepage } from "../../../api";
import { colors } from "../../../styles";

interface EditHomepageModalProps {
    isOpen: boolean;
    onClose: () => void;
    companyId: string;
    companyName: string;
    homepage?: string;
}

interface IEditHomepageForm {
    homepage: string;
}

export default function EditHomepageModal(props: EditHomepageModalProps) {

    const queryClient = useQueryClient();
    const toast = useToast();

    const { register, handleSubmit, reset, watch, formState: { isValid } } = useForm<IEditHomepageForm>({
        mode: "onChange",
        defaultValues: {
            homepage: props.homepage ?? "",
        },
    });

    useEffect(() => {
        if (props.isOpen) {
            reset({ homepage: props.homepage ?? "" });
        }
    }, [props.isOpen, props.homepage, reset]);

    const currentValue = watch("homepage") ?? "";
    const originalValue = props.homepage ?? "";
    const isChanged = currentValue.trim() !== originalValue.trim();

    const updateHomepageMutation = useMutation({
        mutationFn: adminUpdateCompanyHomepage,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetCompanies'] });
            props.onClose();
            toast({
                title: "홈페이지를 저장했습니다.",
                status: "success",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
        onError: (error) => {
            console.error(error);
            toast({
                title: "홈페이지 저장에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const onSubmit = (data: IEditHomepageForm) => {
        updateHomepageMutation.mutate({
            companyId: props.companyId,
            homepage: data.homepage.trim(),
        });
    };

    return (
        <Modal isOpen={props.isOpen} onClose={props.onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text fontSize={20} fontWeight={700} color={colors.black}>
                        {props.companyName || "-"}
                    </Text>
                    <ModalCloseButton />
                </ModalHeader>
                <ModalBody>
                    <Stack gap={3}>
                        <Text fontSize={14} fontWeight={400} color={colors.gray}>
                            홈페이지
                        </Text>
                        <Input
                            {...register("homepage", { required: true, minLength: 1 })}
                            type="text"
                            placeholder="홈페이지 URL을 입력해주세요."
                        />
                    </Stack>
                </ModalBody>
                <Stack marginTop={5} width={"100%"} padding={3}>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        isLoading={updateHomepageMutation.isPending}
                        isDisabled={!isValid || !isChanged}
                        width={"100%"}
                        backgroundColor={colors.blue}
                        color={colors.white}
                        borderRadius={10}
                        _hover={{ bg: colors.blue }}
                        boxShadow="sm"
                    >
                        {originalValue ? "수정" : "등록"}
                    </Button>
                </Stack>
            </ModalContent>
        </Modal>
    );
}
